import { useMemo } from "react";
import type { FusionHubExtendedIntegration } from "src/common/service/fusionHub/fusionHubApi.js";
import { useGetDummyIntegrationsQuery } from "src/common/service/fusionHub/integrations/dummyApi.js";
import {
	asSourceSystemName,
	type FusionhubIntegration,
	type SourceSystemName,
} from "src/common/service/fusionHub/types.js";

export const DUMMY = asSourceSystemName("DUMMY");

/**
 * The source whose logo and name to show for an integration: for a Dummy, the
 * one it was created to look like (FusionHub keeps it in `values`), otherwise
 * its own type. Display only — behaviour per source keeps using `type`.
 */
export const getDisplayedSourceSystem = (
	integration: Pick<FusionhubIntegration, "type" | "values">,
): SourceSystemName => {
	const displayType = integration.values?.displayType;
	return integration.type === DUMMY &&
		typeof displayType === "string" &&
		displayType !== ""
		? asSourceSystemName(displayType)
		: integration.type;
};

/**
 * {@link getDisplayedSourceSystem} for the Dummies in an integration list, by
 * id — list routes don't carry `values`, so each Dummy is fetched once.
 */
export const useDummyDisplayTypes = (
	items: FusionHubExtendedIntegration[] | undefined,
): ReadonlyMap<number, SourceSystemName> => {
	const integrationIds = (items ?? [])
		.filter(({ integration }) => integration.type === DUMMY)
		.map(({ integration }) => integration.id)
		.sort((a, b) => a - b);
	const { data: dummies } = useGetDummyIntegrationsQuery(
		{ integrationIds },
		{ skip: integrationIds.length === 0 },
	);
	return useMemo(
		() =>
			new Map(
				(dummies ?? []).map((dummy) => [
					dummy.id,
					getDisplayedSourceSystem(dummy),
				]),
			),
		[dummies],
	);
};
